import clsx from 'clsx';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AdyenDropIn } from 'src/components/AdyenPayment/AdyenDropIn';
import { SellerAccountEmployeeSelector } from 'src/components/Selectors/SellerAccountEmployeeSelector';
import { Content, useContent } from 'src/contexts/ContentContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Detail } from 'src/modals/common';
import { SectionContent } from 'src/modals/common/Detail/Detail.styled';
import { DetailSection } from 'src/modals/common/DetailSection';
import { FULFILLMENT_PROVIDER_SELLER_USER_IDS } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, TicketType } from 'src/WebApiController';

import { RetailSaleFormValues } from '../types';
import * as styles from './RetailSaleCheckoutStep.css';

// TODO: Add ContentId for ticket type labels
const TICKET_TYPE_OPTIONS: Record<TicketType, string> = {
  [TicketType.ETicket]: 'E-Ticket',
  [TicketType.Paper]: 'Paper',
  [TicketType.Transfer]: 'Transfer',
  [TicketType.Barcode]: 'Barcode',
  [TicketType.QRCode]: 'QR Code',
  [TicketType.Custom]: 'Custom',
  [TicketType.ETicketUrl]: 'E-Ticket URL',
  [TicketType.Season]: 'Season',
  [TicketType.SMPWallet]: 'SMP Wallet',
};

type AdyenSession = {
  sessionId: string;
  sessionData: string;
  clientKey: string;
  environment: string;
  merchantReference: string;
};

// TODO: Replace with real SavedPaymentMethod type from API when the endpoint is available
type SavedPaymentMethod = {
  id: string;
  brand: string;
  lastFour: string;
  expiryMonth: string;
  expiryYear: string;
  isDefault: boolean;
};

type RetailSaleCheckoutStepProps = {
  adyenSession: AdyenSession | null;
  showAdyenDropIn: boolean;
  onAdyenPaymentCompleted: (result: {
    resultCode: string;
    sessionResult?: string;
    billingAddress?: {
      street?: string;
      houseNumberOrName?: string;
      city?: string;
      stateOrProvince?: string;
      postalCode?: string;
      country?: string;
    };
    cardHolderName?: string;
  }) => void;
  onAdyenError: (error: { message: string }) => void;
  onAddNewCard: () => void;
};

export const RetailSaleCheckoutStep = ({
  adyenSession,
  showAdyenDropIn,
  onAdyenPaymentCompleted,
  onAdyenError,
  onAddNewCard,
}: RetailSaleCheckoutStepProps) => {
  const { register, setValue, watch, getValues } =
    useFormContext<RetailSaleFormValues>();

  const hasFulfillmentProvidersFeature = useUserHasFeature(
    Feature.FulfillmentProviders
  );

  const customerInfoMsg = useContent(ContentId.CustomerInfo);
  const deliveryMsg = useContent(ContentId.Delivery);
  const ticketTypeMsg = useContent(ContentId.TicketType);
  const earningsMsg = useContent(ContentId.Earnings);
  const chargeTodayMsg = useContent(ContentId.ChargeToday);
  const paymentMethodMsg = useContent(ContentId.PaymentMethod);
  const paymentTermsMsg = useContent(ContentId.PaymentTerms);
  const addNewCardMsg = useContent(ContentId.AddNewCard);
  const overviewMsg = useContent(ContentId.Overview);
  const ratePercentMsg = useContent(ContentId.RatePercent);
  const amountMsg = useContent(ContentId.Amount);
  const referenceMsg = useContent(ContentId.Reference);
  const addCreditChargeMsg = useContent(ContentId.AddCreditCharge);
  const descriptionMsg = useContent(ContentId.Description);

  const lineItems = watch('lineItems');
  const fulfillerId = watch('fulfillerSellerUserId');
  const ticketType = watch('ticketType');

  const totalAmount =
    lineItems?.reduce((sum, item) => sum + (item.amount ?? 0), 0) ?? 0;

  // TODO: Replace empty array with API hook result when the saved payment methods
  // endpoint is available (e.g. useGetBuyerPaymentMethods(buyerEmail)).
  const savedPaymentMethods: SavedPaymentMethod[] = [];

  // When there are no saved cards, "new card" is the only option; pre-select it.
  const [selectedPaymentId, setSelectedPaymentId] = useState<string>(
    savedPaymentMethods.length === 0 ? 'new' : savedPaymentMethods[0].id
  );

  const isNewCardSelected = selectedPaymentId === 'new' || showAdyenDropIn;

  return (
    <Stack direction="column" gap="l">
      {/* Customer Info Card */}
      {(getValues('buyerName') || getValues('buyerEmail')) && (
        <DetailSection name={customerInfoMsg}>
          <SectionContent numOfColumns={1}>
            <Stack direction="column" gap="s">
              {getValues('buyerName') && (
                <span className={styles.customerName}>
                  {getValues('buyerName')}
                </span>
              )}
              {getValues('buyerEmail') && (
                <span className={styles.customerEmail}>
                  {getValues('buyerEmail')}
                </span>
              )}
            </Stack>
          </SectionContent>
        </DetailSection>
      )}

      {/* DELIVERY section */}
      <DetailSection name={deliveryMsg}>
        <SectionContent numOfColumns={2}>
          <PosFormField label={ticketTypeMsg}>
            <PosEnumSelect<TicketType>
              style={{ width: '100%' }}
              value={(ticketType as TicketType | undefined) ?? null}
              valueOptionsContent={TICKET_TYPE_OPTIONS}
              onChange={(newValue) => {
                setValue('ticketType', newValue ?? undefined);
              }}
            />
          </PosFormField>
          <Detail
            label={<Content id={ContentId.FulfilledBy} />}
            detail={
              <SellerAccountEmployeeSelector
                value={fulfillerId}
                enableEmptySelection
                style={{ width: '100%' }}
                onChange={(newId) => {
                  if (newId !== fulfillerId) {
                    setValue('fulfillerSellerUserId', newId, {
                      shouldDirty: true,
                    });
                    if (FULFILLMENT_PROVIDER_SELLER_USER_IDS.has(newId)) {
                      setValue('isNoFulfill', true, {
                        shouldDirty: true,
                      });
                    }
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                isFulfillmentSection={hasFulfillmentProvidersFeature}
              />
            }
          />
        </SectionContent>
      </DetailSection>

      {/* EARNINGS section */}
      <DetailSection name={earningsMsg}>
        <SectionContent numOfColumns={1}>
          <EarningsTable
            lineItems={lineItems ?? []}
            onAddLineItem={() => {
              const currentItems = getValues('lineItems') ?? [];
              setValue('lineItems', [
                ...currentItems,
                { description: '', amount: 0 },
              ]);
            }}
            onUpdateLineItem={(index, field, value) => {
              const currentItems = getValues('lineItems') ?? [];
              const updatedItems = currentItems.map((item, i) => {
                if (i !== index) return item;
                return { ...item, [field]: value };
              });
              setValue('lineItems', updatedItems);
            }}
            onRemoveLineItem={(index) => {
              const currentItems = getValues('lineItems') ?? [];
              setValue(
                'lineItems',
                currentItems.filter((_, i) => i !== index)
              );
            }}
            overviewLabel={overviewMsg}
            ratePercentLabel={ratePercentMsg}
            amountLabel={amountMsg}
            referenceLabel={referenceMsg}
            addCreditChargeLabel={addCreditChargeMsg}
            descriptionPlaceholder={descriptionMsg}
          />
          <Stack
            direction="row"
            justifyContent="spaceBetween"
            className={styles.chargeTodayRow}
          >
            <span>{chargeTodayMsg}</span>
            <span>${totalAmount.toFixed(2)}</span>
          </Stack>
        </SectionContent>
      </DetailSection>

      {/* PAYMENT METHOD section */}
      <DetailSection name={paymentMethodMsg}>
        <SectionContent numOfColumns={2}>
          <PosFormField label={paymentTermsMsg}>
            <PosTextField
              type="number"
              {...register('paymentTermsDays', { valueAsNumber: true })}
            />
          </PosFormField>
        </SectionContent>

        <div className={styles.savedCardsSection}>
          {/* Saved payment cards list */}
          {/* TODO: Populate savedPaymentMethods from API hook once
              useGetBuyerPaymentMethods(buyerEmail) endpoint is available */}
          {savedPaymentMethods.map((card) => (
            <div
              key={card.id}
              role="radio"
              aria-checked={selectedPaymentId === card.id}
              tabIndex={0}
              className={clsx(styles.savedCardRow, {
                [styles.savedCardRowSelected]: selectedPaymentId === card.id,
              })}
              onClick={() => setSelectedPaymentId(card.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setSelectedPaymentId(card.id);
                }
              }}
            >
              <input
                type="radio"
                readOnly
                checked={selectedPaymentId === card.id}
                aria-hidden="true"
              />
              <div className={styles.savedCardInfo}>
                <span className={styles.savedCardNumber}>
                  {card.brand} **** {card.lastFour}
                </span>
                <span className={styles.savedCardExpiry}>
                  Expires {card.expiryMonth}/{card.expiryYear}
                </span>
              </div>
              {card.isDefault && (
                <span className={styles.savedCardBadge}>Default</span>
              )}
            </div>
          ))}

          {/* Add a new card option */}
          <button
            type="button"
            onClick={() => {
              setSelectedPaymentId('new');
              onAddNewCard();
            }}
            className={clsx(styles.addNewCardRow, {
              [styles.addNewCardRowSelected]: isNewCardSelected,
            })}
          >
            <input
              type="radio"
              readOnly
              checked={isNewCardSelected}
              aria-hidden="true"
            />
            {addNewCardMsg}
          </button>
        </div>

        {/* Adyen Drop-in — shown only when "Add a new card" is selected and session is ready */}
        {showAdyenDropIn && adyenSession && (
          <div className={styles.adyenDropInContainer}>
            <AdyenDropIn
              sessionId={adyenSession.sessionId}
              sessionData={adyenSession.sessionData}
              clientKey={adyenSession.clientKey}
              environment={adyenSession.environment}
              onPaymentCompleted={onAdyenPaymentCompleted}
              onError={onAdyenError}
            />
          </div>
        )}
      </DetailSection>
    </Stack>
  );
};

type EarningsTableProps = {
  lineItems: Array<{
    description: string;
    ratePercent?: number;
    amount: number;
    reference?: string;
  }>;
  onAddLineItem: () => void;
  onUpdateLineItem: (
    index: number,
    field: string,
    value: string | number
  ) => void;
  onRemoveLineItem: (index: number) => void;
  overviewLabel: string;
  ratePercentLabel: string;
  amountLabel: string;
  referenceLabel: string;
  addCreditChargeLabel: string;
  descriptionPlaceholder: string;
};

const EarningsTable = ({
  lineItems,
  onAddLineItem,
  onUpdateLineItem,
  onRemoveLineItem,
  overviewLabel,
  ratePercentLabel,
  amountLabel,
  referenceLabel,
  addCreditChargeLabel,
  descriptionPlaceholder,
}: EarningsTableProps) => {
  return (
    <div className={styles.earningsTableContainer}>
      <table className={styles.earningsTable}>
        <thead>
          <tr className={styles.tableHeaderRow}>
            <th className={styles.tableHeaderLeft}>{overviewLabel}</th>
            <th className={styles.tableHeaderRight}>{ratePercentLabel}</th>
            <th className={styles.tableHeaderRight}>{amountLabel}</th>
            <th className={styles.tableHeaderLeft}>{referenceLabel}</th>
            <th className={styles.tableHeaderDelete} />
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => (
            <tr key={idx} className={styles.tableRow}>
              <td className={styles.tableCell}>
                <input
                  type="text"
                  value={item.description}
                  placeholder={descriptionPlaceholder}
                  onChange={(e) =>
                    onUpdateLineItem(idx, 'description', e.target.value)
                  }
                  className={styles.inlineInput}
                />
              </td>
              <td className={styles.tableCellRight}>
                <input
                  type="number"
                  value={item.ratePercent ?? ''}
                  placeholder="0"
                  onChange={(e) =>
                    onUpdateLineItem(
                      idx,
                      'ratePercent',
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className={styles.inlineInputNarrow}
                />
              </td>
              <td className={styles.tableCellRight}>
                <input
                  type="number"
                  value={item.amount}
                  placeholder="0.00"
                  onChange={(e) =>
                    onUpdateLineItem(
                      idx,
                      'amount',
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className={styles.inlineInputMedium}
                />
              </td>
              <td className={styles.tableCell}>
                <input
                  type="text"
                  value={item.reference ?? ''}
                  placeholder={referenceLabel}
                  onChange={(e) =>
                    onUpdateLineItem(idx, 'reference', e.target.value)
                  }
                  className={styles.inlineInput}
                />
              </td>
              <td className={styles.tableCellDelete}>
                <button
                  type="button"
                  onClick={() => onRemoveLineItem(idx)}
                  className={styles.removeLineItemButton}
                >
                  &times;
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={onAddLineItem}
        className={styles.addLineItemButton}
      >
        {addCreditChargeLabel}
      </button>
    </div>
  );
};
